import React from "react";
import { Helmet } from "react-helmet-async";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import "../styles/Services.css";

const CORE = [

{
    badge:       "BREAD & BUTTER",
    title:       "Starter & Business Sites",
    price:       "€800 – €3,500",
    timeline:    "2 – 4 weeks",
    deliverable: "Deployed Next.js site + CMS handoff",
    accent:      "green",
    bullets: [
      "Next.js + Sanity CMS (client-editable)",
      "SEO-ready, performance-optimised",
      "Secure auth & contact flows",
      "Responsive & accessible",
      "Care Plan attached on handoff",
    ],
  },


  {
    badge:       "UNIQUE OFFER",
    title:       "AI Security Audit",
    price:       "€300 – €800",
    timeline:    "3 – 5 days",
    deliverable: "Plain-English report + prioritised fix list",
    accent:      "gold",
    bullets: [
      "Automated scanning with Semgrep & Snyk",
      "Manual OWASP Top 10 review",
      "AI-generated code pattern analysis",
      "LLM prompt injection & data leakage checks",
      "Executive summary + developer remediation guide",
    ],
  },

  /* */
  {
    badge:       "RECURRING",
    title:       "Care Plan & Maintenance",
    price:       "€150 – €300 / mo",
    timeline:    "Ongoing",
    deliverable: "Monthly health report + uptime monitoring",
    accent:      "blue",
    featured:    true,
    bullets: [
      "Uptime & performance monitoring",
      "Dependency updates & security patching",
      "Monthly health-check report to client",
      "Emergency response SLA",
      "Quarterly security posture review",
    ],
  },
  
];

const ADDONS = [
  {
    title:       "Secure Code Review",
    price:       "€300 – €700",
    timeline:    "1 – 3 days",
    deliverable: "Annotated report + severity matrix",
    bullets: [
      "Authentication & session management audit",
      "API endpoint hardening review",
      "Input validation & injection surface check",
      "OWASP-mapped findings with fix guidance",
    ],
  },
  {
    title:       "Web App QA & Testing",
    price:       "€400 – €1,000",
    timeline:    "3 – 7 days",
    deliverable: "Test report + bug tracker export",
    bullets: [
      "Functional & regression test coverage",
      "Cross-browser & device testing",
      "Security-focused edge-case testing",
      "Documented test plan for handoff",
    ],
  },

  /*
  {
    title:       "AI Feature Integration",
    price:       "€500 – €2,000",
    timeline:    "1 – 3 weeks",
    deliverable: "Live feature + integration docs",
    bullets: [
      "LLM integration (OpenAI / Claude / custom)",
      "RAG pipeline & semantic search",
      "Prompt engineering & context management",
      "Scoped tightly — one feature per engagement",
    ],
  },*/
];

const ACCENT = {
  gold:  "var(--primary-gold, #e9d35b)",
  blue:  "var(--cyber-blue,   #38bdf8)",
  green: "#4ade80",
};

function ServiceCard({ svc }) {
  const accent = ACCENT[svc.accent] || "var(--primary-gold, #e9d35b)";
  return (
    <div
      className={`svc-card ${svc.featured ? "svc-card--featured" : ""}`}
      style={{ "--accent": accent }}
    >
      {svc.badge && <span className="svc-badge">{svc.badge}</span>}
      <h2 className="svc-title">{svc.title}</h2>

      <div className="svc-meta">
        <div className="svc-meta-item">
          <span className="svc-meta-label">PRICE</span>
          <span className="svc-meta-value">{svc.price}</span>
        </div>
        <div className="svc-meta-item">
          <span className="svc-meta-label">TIMELINE</span>
          <span className="svc-meta-value">{svc.timeline}</span>
        </div>
        <div className="svc-meta-item svc-meta-full">
          <span className="svc-meta-label">DELIVERABLE</span>
          <span className="svc-meta-value">{svc.deliverable}</span>
        </div>
      </div>

      <ul className="svc-bullets">
        {svc.bullets.map((b, i) => (
          <li key={i}>
            <CheckCircleOutlineIcon className="svc-check" />
            {b}
          </li>
        ))}
      </ul>

      <a href="mailto:thumbikonyasulu@ymail.com" className="svc-cta">
        <MailOutlineIcon style={{ fontSize: 16 }} />
        Get In Touch
      </a>
    </div>
  );
}

function AddonCard({ svc }) {
  return (
    <div className="svc-addon-card">
      <div className="svc-addon-header">
        <h3>{svc.title}</h3>
        <span className="svc-addon-price">{svc.price}</span>
      </div>
      <p className="svc-addon-meta">
        <span>{svc.timeline}</span>
        <span className="svc-addon-sep">·</span>
        <span>{svc.deliverable}</span>
      </p>
      <ul className="svc-addon-bullets">
        {svc.bullets.map((b, i) => (
          <li key={i}>
            <CheckCircleOutlineIcon className="svc-check-sm" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Services() {
  return (
    <div className="services-page">
      <Helmet>
        <title>Services | Biko — Full-Stack, Security & AI</title>
        <meta name="description" content="Full-stack development, AI security audits, secure code review, and care plans. Dublin-based, remote-friendly." />
        <meta property="og:title"       content="Services | Biko Sulu" />
        <meta property="og:description" content="Full-stack development, AI security audits, and ongoing care plans. Remote-friendly from Dublin." />
        <meta property="og:url"         content="https://bikosulu.com/services" />
      </Helmet>

      <div className="services-inner">

        {/* ── Hero ── */}
        <div className="services-hero">
          <span className="services-eyebrow">WHAT I OFFER</span>
          <h1>Services &amp; <span>Pricing</span></h1>
          <p>End-to-end capability across engineering and security. Remote-friendly, based in Dublin.</p>
        </div>

        {/* ── Core services ── */}
        <section className="services-section">
          <h2 className="services-section-title">Core Stack</h2>
          <div className="svc-grid">
            {CORE.map((s) => <ServiceCard key={s.title} svc={s} />)}
          </div>
        </section>

        {/* ── Add-ons ── */}
        <section className="services-section">
          <h2 className="services-section-title">Add-Ons</h2>
          <div className="svc-addon-grid">
            {ADDONS.map((s) => <AddonCard key={s.title} svc={s} />)}
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <div className="services-cta">
          <h2>Ready to work together?</h2>
          <p>Most engagements start with a free 20-minute scoping call.</p>
          <a href="mailto:thumbikonyasulu@ymail.com" className="services-cta-btn">
            Get In Touch
          </a>
        </div>

      </div>
    </div>
  );
}

export default Services;